/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import { superAgentRequestHandler as requestHandler } from './superAgentRequestHandler.js';
import { SET_NOT_FOUND } from './globalErrorReducer';
import moment from 'moment';
import _ from 'lodash';

function generateId() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 10; i++) {
    id += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return id;
}

// Whitelist of fields to eagerly convert into moment objects
const momentWhitelist = [
  'startTime',
  'endTime',
  'startDate',
  'endDate',
  'createdAt',
  'updatedAt',
  'plannedEndTime',
  'plannedStartTime',
  'timestamp'
];

/**
 * Converts datetime like values in response to moment objects.
 *
 * Mutates the response
 *
 * @param  {Object} object Response object
 */
function momentize(object) {
  return _.forEach(object, (value, key, collection) => {
    // if object or array then recursively scann
    if (typeof value === 'object') return momentize(value);

    if (momentWhitelist.indexOf(key) > -1) {
      // test string to see if valid date, if so, then replace
      // adding true makes moment use strict comparison
      const momentValue = moment(value, moment.ISO_8601, true);

      if (momentValue.isValid()) collection[key] = momentValue;
    }
  });
}

export const INITIALIZATION = '@@requests/INITIALIZATION';
export function initializeRequest(request) {
  const requestAction = {
    type: INITIALIZATION,
    key: request.key || generateId(),
  };

  if (request.method) requestAction.method = request.method;
  if (request.url) requestAction.url = request.url;
  if (request.headers) requestAction.headers = request.headers;
  if (request.query) requestAction.query = request.query;
  if (request.body) requestAction.body = request.body;

  return requestAction;
}

export const REQUEST = '@@requests/REQUEST';
export const RESPONSE = '@@requests/RESPONSE';
export const ERROR = '@@requests/ERROR';
export function handleRequest(key, request) {
  return function(dispatch) {

    const requestAction = {
      type: REQUEST,
      key,
    };

    if (request.method) requestAction.method = request.method;
    if (request.url) requestAction.url = request.url;
    if (request.headers) requestAction.headers = request.headers;
    if (request.query) requestAction.query = request.query;
    if (request.body) requestAction.body = request.body;

    dispatch(requestAction);

    return requestHandler(request)
    .catch((error) => {
      const errorAction = {
        type: ERROR,
        key,
        error,
      };

      if (error.response) {
        errorAction.body = momentize(error.response.body);
        errorAction.headers = error.response.headers;
        errorAction.status = error.response.status;
      }

      if (error.status === 404) {
        dispatch({ type: SET_NOT_FOUND });
      }

      dispatch(errorAction);
      return error;
    })
    .then((response) => {
      // If response is error, then don't dispatch response action
      if (response instanceof Error) return;

      const responseAction = {
        type: RESPONSE,
        key,
        body: momentize(response.body),
        headers: response.headers,
        status: response.status
      };

      dispatch(responseAction);

      if (request.onResponse) request.onResponse(response.body, dispatch);
      return responseAction.body;
    });
  };
}

export const RESET = '@@requests/RESET'
export function handleReset(key) {
  const action = {
    type: RESET,
    key,
  };
  return action;
}
